import React, { useCallback, useMemo, useState } from 'react';
import { IModifierForAssign } from 'types';
import { Checkbox } from 'antd';
import Text from 'components/Text/Text';
import SearchField from 'components/SearchField/SearchField';
import FormSelect from 'components/Select/FormSelect';
import style from './MultipleSelect.module.scss';

interface IProps {
	data: IModifierForAssign[];
	refetch: () => void;
	responseError: boolean;
	setModifiers: (value: string[]) => void;
	IDsSelectedModifiers: string[];
}

export const MultipleSelect: React.FC<IProps> = React.memo(
	({ data, refetch, responseError, setModifiers, IDsSelectedModifiers }) => {
		const [searchString, setSearchString] = useState<string>('');

		const filteredModifiers = useMemo(() => {
			return searchString
				? data.filter((el) => el.name.toLowerCase().includes(searchString.toLowerCase()))
				: data;
		}, [searchString, data]);

		const handleSelectAllModifiers = useCallback(() => {
			if (data.length === IDsSelectedModifiers.length) {
				setModifiers([]);
			} else {
				const IDsOfAllModifiers = data.map((el) => el.id);
				setModifiers(IDsOfAllModifiers);
			}
		}, [setModifiers, data, IDsSelectedModifiers]);

		const renderSelectMenu = useCallback(
			(menu: React.ReactElement) => {
				const getIsCheckedByHasAccess = () => {
					return !!(data.length && data.length === IDsSelectedModifiers.length);
				};

				return (
					<>
						<div className={style.selectMenuHeader}>
							<Checkbox
								className={style.checkBox}
								checked={getIsCheckedByHasAccess()}
								disabled={!data.length}
								onChange={handleSelectAllModifiers}>
								<Text
									translationNameSpace="form"
									translationKey="selectAllModifiersLabel"
									asFragment={true}
								/>
							</Checkbox>
							<SearchField
								onChangeSelectSearchString={setSearchString}
								searchString={searchString}
								className={style.searchField}
							/>
						</div>
						{menu}
					</>
				);
			},
			[data, IDsSelectedModifiers, handleSelectAllModifiers, setSearchString, searchString],
		);

		return (
			<FormSelect
				testId="MultipleSelect"
				name="IDsSelectedModifiers"
				label="modifiersLabel"
				options={filteredModifiers}
				fetchError={responseError}
				onRefetch={refetch}
				onChange={setModifiers}
				counterValue={IDsSelectedModifiers.length}
				dropdownRender={renderSelectMenu}
				mode="multiple"
			/>
		);
	},
);
